import Constants from 'expo-constants';
import Fonts from '../partners/config/fonts';
import { lighten } from './utils/colors';
import {
  PixelRatio,
  Platform,
  StyleSheet,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { SupplierTheme } from '../partners/config/types';
import { SvgProps } from 'react-native-svg';

export type ThemedSVGProps = SvgProps & {
  accentFill?: string;
};

export const PEPPER_GREEN = '#79d97c';

const DEFAULT_THEME: SupplierTheme = {
  ...Fonts.geist,
  colors: {
    accent: PEPPER_GREEN,
    accentText: '#363840',
    authBackground: '#f5f5f5',
    authInputBorder: '#ffffff',
    primary: '#363840',
  },
  images: {},
};

export const theme: SupplierTheme = {
  ...DEFAULT_THEME,
  ...(Platform.OS === 'web'
    ? window?.__PEPPER__?.theme
    : Constants.expoConfig?.extra?.theme),
};

// 90% lighter
export const ACCENT_LIGHT_OPACITY = 0.1;
// 60% lighter
export const ACCENT_MEDIUM_OPACITY = 0.4;
// 30% lighter
export const ACCENT_HEAVY_OPACITY = 0.7;

export const COLORS = {
  accent: theme.colors.accent,
  accentHeavy: lighten(theme.colors.accent, ACCENT_HEAVY_OPACITY),
  accentLight: lighten(theme.colors.accent, ACCENT_LIGHT_OPACITY),
  accentMedium: lighten(theme.colors.accent, ACCENT_MEDIUM_OPACITY),
  accentText: theme.colors.accentText,
  alertNegative: '#ffc1b4',
  alertPositive: '#d2f8d4',
  alertWarning: '#fff6a9',
  allergen: '#c0f0ff',
  authBackground: theme.colors.authBackground,
  authInputBorder: theme.colors.authInputBorder,
  availabilityBackground: '#d2f8d4',
  background: '#f5f5f5',
  black: '#000000',
  blue: '#50a0ea',
  blueAction: '#f5fbff',
  blueActionHeader: '#0b78c1',
  blueAttention: '#24c0fd',
  caution: '#f57e0f',
  cautionHighlight: '#fdefe1',
  chatBlue: '#6bcaff',
  darkGreen: '#00935e',
  divider: '#eeeef0',
  imageBorder: lighten('black', ACCENT_LIGHT_OPACITY),
  inactiveFill: '#c4c4c4',
  inactiveText: '#9a9a9a',
  inactiveTextPMC: '#666',
  mapLocationBlue: '#3861F6',
  negative: '#dc1414',
  notepad: '#FFFEDB',
  offWhite: '#f5f5f5',
  onHoldHeading: '#ffab9f',
  orderStatusBlue: '#0bb5ea',
  overlay: '#000000bb',
  pepperGreen: PEPPER_GREEN,
  positive: '#12bd23',
  primary: theme.colors.primary,
  primaryOverlay: '#36384080',
  promotionLabel: '#ffd493',
  recording: '#ffa6a6',
  salesRepBackground: '#f3f7fa',
  secondary: '#363840',
  secondaryText: '#54565d',
  selectedLocationBackground: '#e3f9e5',
  stroke: '#e2e2e2',
  tileColor: '#F2F7F7',
  transparent: 'transparent',
  viewedBySupplier: '#ebebeb',
  warning: '#ffd130',
  warningBoxShadow: '#ffc12133',
  warningContent: '#f57e0f',
  white: '#ffffff',
};

export const FONTS: { [key: string]: TextStyle } = {
  helvetica: {
    fontFamily: 'Helvetica-Regular',
  },
  helveticaBlack: {
    fontFamily: 'Helvetica-Black',
  },
  primary: {
    fontFamily: theme.fonts.primary,
  },
  primaryBold: {
    fontFamily: theme.fonts.primaryBold,
  },
  primarySemiBold: {
    fontFamily: theme.fonts.primarySemiBold,
  },
};

export const AUTH_STYLES = StyleSheet.create({
  inputContainer: {
    borderColor: COLORS.authInputBorder,
  },
});

export const LAYOUT_MAX_WIDTH = 1120;

export const LARGE_LAYOUT_CONTENT_MAX_WIDTH = 640;

export const UPDATE_ORDER_GUIDE_ACTION_SHEET_MAX_WIDTH = 444;

export const DRAWER_HEADER_HEIGHT = 64;
export const PAGE_CONTAINER_HEADER_HEIGHT = 52;

export const HEADER_BUTTON_STYLE = {
  paddingRight: 6,
  paddingVertical: 16,
};

export const BACK_BUTTON_SIZE = 32;
export const CLEAR_FILTERS_BUTTON_SIZE = 32;
export const LIST_GAP_HORIZONTAL = 8;
export const LIST_MARGIN_HORIZONTAL = 16;
export const LIST_ITEM_PADDING = 24;
export const SPLIT_CONTAINER_MARGIN_HORIZONTAL = 24;
export const SCREEN_PADDING_XS = 8;
export const SCREEN_PADDING_SMALL = 16;
export const SCREEN_PADDING = 24;
export const SCREEN_PADDING_LARGE = 32;
export const SCREEN_PADDING_XL = 48;

export const HEADER_PADDING_HORIZONTAL_SMALL = 16;
export const HEADER_PADDING_HORIZONTAL_LARGE = 24;

export const LIST_ITEM_SEPARATOR_HEIGHT = 1;

export const ACTION_SHEET_MAX_HEIGHT = 1200;

export const BORDER_RADIUS_LARGE = 24;
export const BORDER_RADIUS_MEDIUM = 16;
export const BORDER_RADIUS_SMALL = 8;
export const BORDER_RADIUS_TINY = 4;

const themeTypography = {
  h1: {
    ...theme.typography.h1,
    color: COLORS.primary,
  },
  h2: {
    ...theme.typography.h2,
    color: COLORS.primary,
  },
  large: {
    ...theme.typography.large,
    color: COLORS.primary,
  },
  medium: {
    ...theme.typography.medium,
    color: COLORS.primary,
  },
  small: {
    ...theme.typography.small,
    color: COLORS.primary,
  },
  xsmall: {
    ...theme.typography.xsmall,
    color: COLORS.primary,
  },
};

export const TYPOGRAPHY = StyleSheet.create({
  ...themeTypography,
  actionSheetMessageText: {
    ...themeTypography.medium,
    color: COLORS.primary,
    textAlign: 'center',
  },
  addSupplierSubtitle: {
    ...FONTS.primary,
    color: COLORS.primary,
    fontSize: theme.fontSizes.medium,
    lineHeight: 24,
    marginBottom: 24,
  },
  brandHeadline: {
    ...FONTS.primarySemiBold,
    color: theme.colors.primary,
    fontSize: 28,
    lineHeight: 36,
  },
  brandHeadlineSmall: {
    ...FONTS.primarySemiBold,
    color: theme.colors.primary,
    fontSize: 18,
    lineHeight: 28,
  },
  cardHeaderText: {
    ...FONTS.primarySemiBold,
    color: COLORS.primary,
    fontSize: 25,
    lineHeight: 30,
    marginBottom: 20,
    textAlign: 'center',
  },
  cardMessageText: {
    ...FONTS.primary,
    color: COLORS.primary,
    fontSize: theme.fontSizes.medium,
    lineHeight: 24,
    textAlign: 'center',
  },
  description: {
    ...FONTS.primary,
    color: COLORS.inactiveText,
    fontSize: theme.fontSizes.small,
  },
  h3: {
    ...FONTS.primarySemiBold,
    color: COLORS.primary,
    fontSize: theme.fontSizes.large,
    lineHeight: 22,
  },
  h3Bold: {
    ...themeTypography.large,
    ...FONTS.primaryBold,
  },
  h4: {
    ...FONTS.primary,
    color: COLORS.primary,
    fontSize: theme.fontSizes.small,
    lineHeight: 16,
  },
  h4SemiBold: {
    ...FONTS.primarySemiBold,
    color: COLORS.primary,
    fontSize: theme.fontSizes.small,
    lineHeight: 16,
  },
  listSmall: {
    ...FONTS.primary,
    color: COLORS.primary,
    fontSize: theme.fontSizes.xsmall,
  },
  listSubtitle: {
    ...FONTS.primary,
    color: COLORS.primary,
    fontSize: theme.fontSizes.small,
    lineHeight: 20,
  },
  listTitle: {
    ...FONTS.primarySemiBold,
    color: COLORS.primary,
    fontSize: theme.fontSizes.medium,
    lineHeight: 24,
  },
  navigationCenterTitle: {
    ...FONTS.primaryBold,
    color: COLORS.primary,
    fontSize: theme.fontSizes.large,
    lineHeight: 22,
  },
  navigationTitle: {
    ...FONTS.primaryBold,
    color: COLORS.primary,
    fontSize: 22,
    lineHeight: 28,
  },
  navigationTitleLarge: {
    ...FONTS.primaryBold,
    color: COLORS.primary,
    fontSize: 32,
    lineHeight: 36,
  },
  newOrderHeader: {
    ...FONTS.primaryBold,
    color: COLORS.primary,
    fontSize: 28,
    lineHeight: 34,
  },
  p: {
    ...FONTS.primary,
    color: COLORS.primary,
    fontSize: theme.fontSizes.medium,
    lineHeight: 19,
  },
  pBold: {
    ...FONTS.primaryBold,
    color: COLORS.primary,
    fontSize: theme.fontSizes.medium,
    lineHeight: 19,
  },
  pSemiBold: {
    ...FONTS.primarySemiBold,
    color: COLORS.primary,
    fontSize: theme.fontSizes.medium,
    lineHeight: 19,
  },
  smallLabel: {
    ...themeTypography.xsmall,
    ...FONTS.primarySemiBold,
    color: COLORS.inactiveText,
    fontSize: 11,
  },
  tabBarLabel: {
    ...themeTypography.small,
    ...FONTS.primarySemiBold,
  },
  tiny: {
    ...FONTS.primary,
    color: COLORS.primary,
    fontSize: 10,
    lineHeight: 12,
  },
  toast: {
    ...FONTS.primarySemiBold,
    color: COLORS.white,
    fontSize: theme.fontSizes.medium,
    lineHeight: 20,
  },
});

export const shadow = ({
  color = COLORS.black,
  elevation,
  opacity,
  radius,
  x,
  y,
}: {
  color?: string;
  elevation?: number;
  opacity: number;
  radius: number;
  x: number;
  y: number;
}) =>
  Platform.select({
    android: {
      elevation,
    },
    default: {
      shadowColor: color,
      shadowOffset: {
        height: y,
        width: x,
      },
      shadowOpacity: opacity,
      shadowRadius: radius,
    },
  });

export const DESCRIPTION_MAX_HEIGHT = 60 * PixelRatio.getFontScale();

export const BORDER = {
  primary: {
    borderColor: COLORS.divider,
    borderRadius: BORDER_RADIUS_SMALL,
    borderWidth: 1,
  },
};

export const focusedItem = (
  outlineOffset = 2,
  paddingLeft = 0,
  paddingRight = 0,
): ViewStyle | undefined => {
  return Platform.select({
    web: {
      borderRadius: 8,
      outlineColor: COLORS.primary,
      outlineOffset,
      outlineStyle: 'solid',
      outlineWidth: 2,
      paddingLeft,
      paddingRight,
    },
  });
};
